<script setup>
import { computed, ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useSmsConsent } from '@/composables/api/smsConsent/useSmsConsent';
import { SMS_CONSENT_STATUS, SMS_CONSENT_ACTION, SMS_CONSENT_METHOD, SMS_CONSENT_METHOD_LABELS, SMS_CONSENT_CHANNELS, renderSmsConsentDisclosure } from '@/fhirworks/constants/smsConsent';

/**
 * Embedded verbal SMS reminder consent capture (issue #4071, FR-4).
 *
 * Renders inside the mobile phone list item of the edit patient dialog. Collapses to a
 * one-line summary once consent has been recorded; click to expand and revise. Inert when
 * no mobile number is on file. Records via the consent endpoint, then emits `recorded` so
 * the parent can refresh the patient (keeps the chip reactive).
 */
const props = defineProps({
    // The fhirworks Patient resource being edited.
    patient: {
        type: Object,
        required: true,
    },
    // The mobile-phone ContactPoint this consent is scoped to.
    contactPoint: {
        type: Object,
        default: null,
    },
});

const emit = defineEmits(['recorded']);

const authStore = useAuthStore();
const { recordConsentEvent, loading, error } = useSmsConsent();

const mobileValue = computed(() => props.contactPoint?.value || null);
const consent = computed(() => props.contactPoint?.smsReminderConsent || null);
const status = computed(() => consent.value?.status || SMS_CONSENT_STATUS.NOT_ASKED);
const hasRecord = computed(() => !!consent.value && status.value !== SMS_CONSENT_STATUS.NOT_ASKED);
const isCarrierOptedOut = computed(() => status.value === SMS_CONSENT_STATUS.OPTED_OUT_CARRIER);

const disclosure = computed(() => renderSmsConsentDisclosure(authStore.account?.name));

// Collapsed by default once a consent record exists; expanded for first capture.
// Derived so it re-evaluates when the contactPoint prop changes (e.g. switching numbers)
// instead of snapshotting hasRecord once at setup.
const userExpanded = ref(false);
const expanded = computed(() => userExpanded.value || !hasRecord.value);
const channel = ref(SMS_CONSENT_CHANNELS[0].value);
const attested = ref(false);

const summaryDate = computed(() => {
    const iso = consent.value?.lastUpdatedAt;
    if (!iso) return '';
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
});

const summaryLabel = computed(() => {
    const map = {
        [SMS_CONSENT_STATUS.OPTED_IN]: 'Opted in',
        [SMS_CONSENT_STATUS.DECLINED]: 'Declined',
        [SMS_CONSENT_STATUS.OPTED_OUT_CARRIER]: 'Opted out',
    };
    const label = map[status.value] || 'Recorded';
    const when = summaryDate.value ? ` on ${summaryDate.value}` : '';
    const methodCode = consent.value?.method;
    const methodLabel = methodCode ? SMS_CONSENT_METHOD_LABELS[methodCode] || methodCode : '';
    const via = methodLabel ? ` via ${methodLabel}` : '';
    return `${label}${when}${via} — click to update`;
});

async function submit(action) {
    if (!mobileValue.value) return;
    try {
        const result = await recordConsentEvent(props.patient.id, {
            telecomValue: mobileValue.value,
            action,
            method: SMS_CONSENT_METHOD.VERBAL,
            channel: channel.value,
            attestationVerbatim: attested.value,
        });
        emit('recorded', result?.consent ?? null);
        attested.value = false;
        userExpanded.value = false;
    } catch {
        // error ref is set by the composable; surfaced inline below.
    }
}
</script>

<template>
    <div class="bn-sms-consent-form">
        <!-- No mobile number: inert prompt -->
        <div v-if="!mobileValue" class="text-medium-emphasis text-caption">Add a mobile phone to enable SMS consent</div>

        <!-- Collapsed summary -->
        <button v-else-if="hasRecord && !expanded" type="button" class="bn-consent-summary text-caption text-primary" @click="userExpanded = true">
            {{ summaryLabel }}
        </button>

        <!-- Full capture form -->
        <div v-else class="bn-consent-expanded">
            <p class="text-caption text-medium-emphasis mb-2">{{ disclosure }}</p>

            <v-alert v-if="isCarrierOptedOut" type="warning" variant="tonal" density="compact" class="mb-2 text-caption">
                This patient previously opted out via SMS. Only they can resume messaging by texting START to your clinic number from their phone.
            </v-alert>

            <v-radio-group v-model="channel" inline hide-details density="compact" class="mt-0 mb-1">
                <v-radio v-for="opt in SMS_CONSENT_CHANNELS" :key="opt.value" :label="opt.label" :value="opt.value"></v-radio>
            </v-radio-group>

            <v-checkbox v-model="attested" hide-details density="compact" class="mt-0" label="I read the script verbatim" data-cy="bnSmsConsentAttestation"></v-checkbox>

            <v-alert v-if="error" type="error" variant="tonal" density="compact" class="my-2 text-caption"> Could not record consent. Please try again. </v-alert>

            <div class="d-flex ga-2 mt-1">
                <v-btn
                    size="small"
                    color="success"
                    variant="flat"
                    :disabled="!attested || isCarrierOptedOut || loading"
                    :loading="loading"
                    data-cy="bnSmsConsentConsentedBtn"
                    @click="submit(SMS_CONSENT_ACTION.GRANTED)"
                >
                    Consented
                </v-btn>
                <v-btn size="small" color="grey" variant="tonal" :disabled="loading" data-cy="bnSmsConsentDeclinedBtn" @click="submit(SMS_CONSENT_ACTION.DECLINED)"> Declined </v-btn>
                <v-btn v-if="hasRecord" size="small" variant="text" :disabled="loading" @click="userExpanded = false"> Cancel </v-btn>
            </div>
        </div>
    </div>
</template>

<style scoped>
.bn-consent-summary {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
}
</style>
